import { v4 as uuid } from "uuid";
import { Event, Adapter } from "../adapter";
import { EwaAnalyticsOptions } from "./interfaces";

import { createEwaAnalyticsSdk, type EwaAnalyticsInitData } from "@2l/ewa-analytics-web-sdk";

export default class EwaAnalyticsAdapter extends Adapter {
  static stream: Uppercase<string> = "EWA_ANALYTICS";
  static trackerNumber = "11";

  environment: string;
  apiUrl: string;
  initData?: EwaAnalyticsInitData;

  private isSDKInitialized = false;
  private initPromise: Promise<void> | null = null;
  private pendingEvents: Event[] = [];

  constructor(options?: EwaAnalyticsOptions) {
    super(options);

    this.environment = options?.ewaAnalytics?.environment ?? "sandbox";
    this.apiUrl =
      this.environment === "production"
        ? "https://api.appewa.com/analytics/v1"
        : "https://api.dev.appewa.com/analytics/v1";
    this.initData = options?.ewaAnalytics;
  }

  async _init() {
    if (this.isSDKInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._callSDKInit();
    return this.initPromise;
  }

  async _invoke(event: Event) {
    if (!this.isSDKInitialized) {
      this.pendingEvents.push(event);

      if (!this.initPromise) {
        this.initPromise = this._callSDKInit();
      }

      if (this.initPromise) {
        try {
          await this.initPromise;
        } catch {
          // Initialization failure will leave events in the queue for retry later
        }
      }

      return;
    }

    await this._sendEvent(event);
  }

  private async _callSDKInit() {
    const sdk = createEwaAnalyticsSdk();

    const userAgent = this.initData?.userAgent ?? navigator.userAgent;

    await sdk.init({
      transport: "http",
      apiUrl: this.apiUrl,
      initData: {
        userAgent,
        randomUserId: this.initData?.randomUserId ?? this.external_id ?? uuid(),
        country: this.initData?.country || "en",
        ip_address: this.initData?.ip_address || "0.0.0.0",
        appVersion: this.initData?.appVersion,
        appId: this.initData?.appId ?? "com.ewa.ewaweb.platform",
        environment: this.environment,
      },
      context: {
        user_agent: userAgent,
      },
    });

    this.client = sdk;
    this.isSDKInitialized = true;
    await this._flushPendingEvents();
  }

  private async _flushPendingEvents() {
    while (this.pendingEvents.length) {
      const event = this.pendingEvents.shift();
      if (!event) continue;

      try {
        await this._sendEvent(event);
      } catch (error) {
        this.pendingEvents.unshift(event);
        break;
      }
    }
  }

  private async _sendEvent(event: Event) {
    if (!this.client) {
      throw new Error("EWA Analytics SDK client must be initialized before");
    }

    const { type, subtype, ...rest} = event.value

    await this.client.trackEvent(
      "event",
      {
        ...rest,
        customer_user_id: this.external_id,
        event_name: event.name,
        event_type: event.value?.type ?? "",
        event_subtype: subtype,
        platform: 'web',
      },
      event.context,
    );
  }

  setCustomerUserId(userId: string) {
    this.external_id = userId;

    if (!this.client) return;
  }
}
